import type { ComponentType } from 'react';
import {
  BehanceLogo,
  DribbbleLogo,
  Envelope,
  FigmaLogo,
  GithubLogo,
  GitlabLogo,
  Globe,
  InstagramLogo,
  LinkedinLogo,
  Link as LinkIcon,
  MediumLogo,
  Newspaper,
  SpotifyLogo,
  TwitterLogo,
  Video,
  YoutubeLogo,
} from '@phosphor-icons/react';
import type { IconProps } from '@phosphor-icons/react';
import type { LinkPlatform } from './schemas';

interface PlatformMeta {
  readonly id: LinkPlatform;
  readonly label: string;
  readonly placeholder: string;
  readonly icon: ComponentType<IconProps>;
}

export const PLATFORM_META: Record<LinkPlatform, PlatformMeta> = {
  website: { id: 'website', label: 'Website', placeholder: 'https://', icon: Globe },
  email: { id: 'email', label: 'Email', placeholder: 'you@domain.com', icon: Envelope },
  github: { id: 'github', label: 'GitHub', placeholder: 'https://github.com/you', icon: GithubLogo },
  gitlab: { id: 'gitlab', label: 'GitLab', placeholder: 'https://gitlab.com/you', icon: GitlabLogo },
  linkedin: {
    id: 'linkedin',
    label: 'LinkedIn',
    placeholder: 'https://linkedin.com/in/you',
    icon: LinkedinLogo,
  },
  twitter: { id: 'twitter', label: 'Twitter', placeholder: 'https://twitter.com/you', icon: TwitterLogo },
  bluesky: {
    id: 'bluesky',
    label: 'Bluesky',
    placeholder: 'https://bsky.app/profile/you',
    icon: LinkIcon,
  },
  mastodon: {
    id: 'mastodon',
    label: 'Mastodon',
    placeholder: 'https://mastodon.social/@you',
    icon: LinkIcon,
  },
  instagram: {
    id: 'instagram',
    label: 'Instagram',
    placeholder: 'https://instagram.com/you',
    icon: InstagramLogo,
  },
  dribbble: {
    id: 'dribbble',
    label: 'Dribbble',
    placeholder: 'https://dribbble.com/you',
    icon: DribbbleLogo,
  },
  behance: {
    id: 'behance',
    label: 'Behance',
    placeholder: 'https://behance.net/you',
    icon: BehanceLogo,
  },
  figma: { id: 'figma', label: 'Figma', placeholder: 'https://figma.com/@you', icon: FigmaLogo },
  youtube: {
    id: 'youtube',
    label: 'YouTube',
    placeholder: 'https://youtube.com/@you',
    icon: YoutubeLogo,
  },
  vimeo: { id: 'vimeo', label: 'Vimeo', placeholder: 'https://vimeo.com/you', icon: Video },
  medium: { id: 'medium', label: 'Medium', placeholder: 'https://medium.com/@you', icon: MediumLogo },
  substack: {
    id: 'substack',
    label: 'Substack',
    placeholder: 'https://you.substack.com',
    icon: Newspaper,
  },
  spotify: {
    id: 'spotify',
    label: 'Spotify',
    placeholder: 'https://open.spotify.com/artist/...',
    icon: SpotifyLogo,
  },
  custom: { id: 'custom', label: 'Custom', placeholder: 'https://', icon: LinkIcon },
};

export const PLATFORM_ORDER: ReadonlyArray<LinkPlatform> = [
  'website',
  'email',
  'github',
  'gitlab',
  'linkedin',
  'twitter',
  'bluesky',
  'mastodon',
  'instagram',
  'dribbble',
  'behance',
  'figma',
  'youtube',
  'vimeo',
  'medium',
  'substack',
  'spotify',
  'custom',
];

export function detectPlatform(href: string): LinkPlatform {
  const v = href.toLowerCase();
  if (v.startsWith('mailto:') || (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))) return 'email';
  if (v.includes('github.com')) return 'github';
  if (v.includes('gitlab.com')) return 'gitlab';
  if (v.includes('linkedin.com')) return 'linkedin';
  if (v.includes('twitter.com') || v.includes('x.com')) return 'twitter';
  if (v.includes('bsky.app')) return 'bluesky';
  if (v.includes('mastodon') || v.includes('@') && v.includes('.social')) return 'mastodon';
  if (v.includes('instagram.com')) return 'instagram';
  if (v.includes('dribbble.com')) return 'dribbble';
  if (v.includes('behance.net')) return 'behance';
  if (v.includes('figma.com')) return 'figma';
  if (v.includes('youtube.com') || v.includes('youtu.be')) return 'youtube';
  if (v.includes('vimeo.com')) return 'vimeo';
  if (v.includes('medium.com')) return 'medium';
  if (v.includes('substack.com')) return 'substack';
  if (v.includes('spotify.com')) return 'spotify';
  if (v.startsWith('http')) return 'website';
  return 'custom';
}
